import React from "react";

import { IProductID } from "../../../models/nominals";
import { useAppDispatch } from "../../../store/reducers";
import {
    trackConfigUpsellAddedEvent,
    trackConfigUpsellNoCheckboxShownEvent,
    trackConfigUpsellRemovedEvent,
    trackConfigUpsellShownEvent,
} from "../../../utils/analytics";
import { setSelectedUpgrade } from "../reducers";

type ShownEventKind = "default" | "no-checkbox";

/**
 * Shared analytics + Redux dispatch contract for configurator upsells.
 *
 * Both `ConfiguratorUpsell` (checkbox / non-checkbox card) and
 * `ConfiguratorUpsellRadio` (1-800-Pillows radio card) need to:
 *   - fire `trackConfigUpsellShownEvent` (or its no-checkbox variant) once
 *     per `configName` change with a non-empty value,
 *   - dispatch `setSelectedUpgrade(id|null)` and the matching
 *     `trackConfigUpsellAdded/RemovedEvent` analytics when the user
 *     selects / clears the upgrade.
 *
 * Note on the shown-event semantics: the hook stores the most recent
 * `configName` it fired for, not a set of every name seen. If the same
 * instance cycles A → B → A, the third render re-fires for "A". This is
 * acceptable because configNames are stable in practice (derived from
 * `lineOne` / `sectionTitle` on a bound bundle), and tracking a full set
 * would carry memory for no observed benefit.
 *
 * Co-locating that contract here prevents the two presentations from
 * drifting in their event semantics over time.
 */
export function useUpsellAnalytics(
    configName: string,
    opts?: { shownEvent?: ShownEventKind },
): {
    selectUpgrade: (productUpgradeID: IProductID) => void;
    clearUpgrade: () => void;
} {
    const dispatch = useAppDispatch();
    const shownEvent: ShownEventKind = opts?.shownEvent ?? "default";

    const hasFiredRef = React.useRef<string | null>(null);
    React.useEffect(() => {
        if (!configName || hasFiredRef.current === configName) {
            return;
        }
        hasFiredRef.current = configName;
        if (shownEvent === "no-checkbox") {
            trackConfigUpsellNoCheckboxShownEvent(configName);
        } else {
            trackConfigUpsellShownEvent(configName);
        }
    }, [configName, shownEvent]);

    const selectUpgrade = React.useCallback(
        (productUpgradeID: IProductID) => {
            dispatch(setSelectedUpgrade(productUpgradeID));
            trackConfigUpsellAddedEvent(configName);
        },
        [dispatch, configName],
    );

    const clearUpgrade = React.useCallback(() => {
        dispatch(setSelectedUpgrade(null));
        trackConfigUpsellRemovedEvent(configName);
    }, [dispatch, configName]);

    return { selectUpgrade, clearUpgrade };
}
